import { useEffect, useRef, useState } from 'react';
import type { GroupFormattedData } from '@/hooks/queries';
import { warehouseGroupSchema } from '../schemas';
import { GroupType } from '@/types/schemas';

const FORM_DRAFT_KEY = 'warehouse-group-form-draft';

interface WarehouseGroupDraft {
  formData: GroupFormattedData;
  groupType: GroupType;
  timestamp: string;
}

export const useFormPersistence = (activeGroup: GroupType) => {
  const [hasDraft, setHasDraft] = useState<boolean>(false);
  const activeGroupRef = useRef<GroupType>(activeGroup);

  useEffect(() => {
    activeGroupRef.current = activeGroup;
  }, [activeGroup]);

  const saveDraft = (formData: GroupFormattedData) => {
    try {
      const draft = {
        formData,
        groupType: activeGroupRef.current,
        timestamp: new Date().toISOString()
      };

      localStorage.setItem(FORM_DRAFT_KEY, JSON.stringify(draft));

      setHasDraft(true);
    } catch (error) {
      console.error('Error saving form draft:', error);
    }
  };

  const loadDraft = (): WarehouseGroupDraft | null => {
    try {
      const draftJson = localStorage.getItem(FORM_DRAFT_KEY);

      if (!draftJson) {
        return null;
      }

      const draft: unknown = JSON.parse(draftJson);

      if (!draft || typeof draft !== 'object' || Array.isArray(draft)) {
        return null;
      }

      const draftRecord = draft as Record<string, unknown>;
      if (draftRecord.groupType !== activeGroupRef.current || typeof draftRecord.timestamp !== 'string') {
        return null;
      }

      const formResult = warehouseGroupSchema.safeParse(draftRecord.formData);
      if (!formResult.success) {
        return null;
      }

      return {
        formData: {
          ...formResult.data,
          loai_nhom: activeGroupRef.current
        },
        groupType: activeGroupRef.current,
        timestamp: draftRecord.timestamp
      };
    } catch (error) {
      console.error('Error loading form draft:', error);
      return null;
    }
  };

  const clearDraft = () => {
    try {
      localStorage.removeItem(FORM_DRAFT_KEY);

      setHasDraft(false);
    } catch (error) {
      console.error('Error clearing form draft:', error);
    }
  };

  useEffect(() => {
    const draft = loadDraft();
    setHasDraft(!!draft);
  }, [activeGroup]);

  return {
    hasDraft,
    saveDraft,
    loadDraft,
    clearDraft
  };
};
